import cx from 'classnames';
import type {
  ChangeEvent,
  ClipboardEventHandler,
  FocusEventHandler,
  FormEventHandler,
  FunctionComponent,
  KeyboardEventHandler,
} from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { determinePossibleCardRole } from '@trello/card-roles';
import { useClickOutside } from '@trello/dom-hooks';
import { useFeatureGate } from '@trello/feature-gate-client';
import type { CurrentBoardFullCardFragment } from '@trello/graphql';
import { getFragmentDocument, optimisticIdManager } from '@trello/graphql';
import { intl } from '@trello/i18n';
import { useBoardId, useListId, useWorkspaceId } from '@trello/id-context';
import { getKey, Key, Scope, useShortcut } from '@trello/keybindings';
import type { Board } from '@trello/model-types';
import { Button } from '@trello/nachos/button';
import { CloseIcon } from '@trello/nachos/icons/close';
import type { HideReasonType } from '@trello/nachos/popover';
import { HideReason, Popover, usePopover } from '@trello/nachos/popover';
import { SPACING } from '@trello/position';
import { SharedState, useSharedState } from '@trello/shared-state';
import { getSmartCardClient } from '@trello/smart-card/smart-card-client';
import { usePersistedState } from '@trello/storage';
import type { ListTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { AutosizeTextarea } from 'app/src/components/AutosizeTextarea';
import { openCardBack } from 'app/src/components/CardFront/openCardBack';
import { cardIdsAddedSinceFilteringSharedState } from 'app/src/components/FilterPopover/cardIdsAddedSinceFilteringSharedState';
import { useIsInboxBoard } from 'app/src/components/Inbox';
import { bypassViewportCheckForListOrCardId } from 'app/src/components/List/useListsAndCardsViewportObserver';
import { usePasteFilesOnList } from 'app/src/components/List/usePasteFilesOnList';
import { viewFiltersContextSharedState } from 'app/src/components/ViewFilters/viewFiltersContextSharedState';
import { useAddCardToListMutation } from './AddCardToListMutation.generated';
import { CardComposerLimitError } from './CardComposerLimitError';
import { cardComposerState, closeCardComposer } from './cardComposerState';
import { createOptimisticCardResponse } from './createOptimisticCardResponse';
import { MAX_CARDS, SaveMultiplePopover } from './SaveMultiplePopover';
import { useCardComposerLimits } from './useCardComposerLimits';
import { usePersistentScrollIntoView } from './usePersistentScrollIntoView';

import styles from './CardComposer.less';

/**
 * Store the input value state across rerenders.
 * Treat this as an internal detail; it's only exported for unit tests.
 */
export const cardComposerInputValueState = new SharedState<string>('');

export interface CardComposerProps {
  prevPosition: number;
  position: number;
  nextPosition: number;
}

export const CardComposer: FunctionComponent<CardComposerProps> = ({
  position,
  nextPosition,
}) => {
  const boardId = useBoardId();
  const listId = useListId();
  const workspaceId = useWorkspaceId();

  const [addCardToList] = useAddCardToListMutation();
  const { pasteFileOnList } = usePasteFilesOnList();

  const ref = useRef<HTMLLIElement>(null);

  const [inputValue] = useSharedState(cardComposerInputValueState);
  const [cardIdsAddedSinceFiltering, setCardIdsAddedSinceFiltering] =
    useSharedState(cardIdsAddedSinceFilteringSharedState);
  const [viewFiltersContext] = useSharedState(viewFiltersContextSharedState);
  const { filters } = viewFiltersContext.viewFilters;

  const [draft, updateDraft] = usePersistedState<{ title: string }>(
    `boardCardComposerSettings-${boardId}`,
    null,
  );

  const { value: isHoverGapEnabled } = useFeatureGate(
    'trello_hover_inbetween_cards',
  );

  const { popoverProps, hide, show, targetRef } =
    usePopover<HTMLButtonElement>();

  useEffect(() => {
    if (draft?.title && !inputValue) {
      cardComposerInputValueState.setValue(draft.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.title]);

  useEffect(() => {
    Analytics.sendScreenEvent({
      name: 'inlineCardComposerInlineDialog',
      containers: formatContainers({ boardId, listId, workspaceId }),
    });
  }, [boardId, listId, workspaceId]);

  const enqueueProgrammaticScrollIntoView = usePersistentScrollIntoView(ref);
  const onFocus = useCallback<FocusEventHandler<HTMLTextAreaElement>>(
    (e) => {
      enqueueProgrammaticScrollIntoView();
      // Set the input cursor to the end of the value. Without this, sometimes the
      // cursor can get displaced across rerenders, causing input to feel janky.
      const currentValue = e.target.value;
      e.target.setSelectionRange(currentValue.length, currentValue.length);
    },
    [enqueueProgrammaticScrollIntoView],
  );

  const isInboxBoard = useIsInboxBoard();

  const saveSingleCard = useCallback(
    async (
      shouldOpenCardBackOnCreation: boolean = false,
      cardName: string,
      updatePosition: number | null = null,
    ) => {
      const traceId = Analytics.startTask({
        taskName: 'create-card/list',
        source: 'inlineCardComposerInlineDialog',
      });

      const isLastCard = !Number.isFinite(nextPosition);

      try {
        const cardRole = determinePossibleCardRole({ name: cardName });

        let newPosition;
        if (updatePosition !== null) {
          newPosition = updatePosition;
        } else if (isLastCard) {
          newPosition = position + SPACING;
        } else {
          newPosition = (position + nextPosition) / 2;
        }

        updateDraft(null);
        cardComposerInputValueState.setValue('');
        cardComposerState.setValue({ position: newPosition });

        const optimisticId = optimisticIdManager.generateOptimisticId('Card');

        const result = await addCardToList({
          variables: {
            idList: listId,
            name: cardName,
            pos: newPosition,
            cardRole,
            traceId,
          },
          optimisticResponse: {
            __typename: 'Mutation',
            createCard: createOptimisticCardResponse({
              id: optimisticId,
              idBoard: boardId,
              idList: listId,
              cardRole,
              name: cardName,
              pos: newPosition,
            }),
          },
          update(cache, { data }) {
            const newCard = data?.createCard;

            if (!newCard) {
              return;
            }

            bypassViewportCheckForListOrCardId(newCard.id);

            // When an `optimisticResponse` is configured on a mutation,
            // `update` is called twice - once with the optimistic ID, and once
            // with the resolved ID.
            if (newCard.id !== optimisticId) {
              optimisticIdManager.resolveId(optimisticId, newCard.id);

              if (filters.isFiltering()) {
                setCardIdsAddedSinceFiltering(
                  cardIdsAddedSinceFiltering.add(newCard.id),
                );
              }
            }

            /**
             * First part of getting optimistic updates to work is adding the card
             * to the set of board.cards, which will refresh the queries that include
             * board.cards. For example, the BoardListsContextQuery.
             */
            cache.modify<Board>({
              id: cache.identify({
                id: boardId,
                __typename: 'Board',
              }),
              fields: {
                cards(existingCards = [], { readField }) {
                  const newCardRef =
                    cache.writeFragment<CurrentBoardFullCardFragment>({
                      data: newCard,
                      fragment: getFragmentDocument('CurrentBoardFullCard'),
                      fragmentName: 'CurrentBoardFullCard',
                    });

                  // Quick safety check - if the new card is already
                  // present in the cache, we don't need to add it again.
                  if (
                    !newCardRef ||
                    existingCards.some(
                      (cardRef) => readField('id', cardRef) === newCard.id,
                    )
                  ) {
                    return existingCards;
                  }

                  // Append the new ref to the array of existing refs for cards
                  return existingCards.concat(newCardRef);
                },
              },
            });
          },
        });

        const newCardId = result.data?.createCard?.id;

        let linkCardProvider = undefined;

        if (cardRole === 'link') {
          const smartCardClient = getSmartCardClient();
          const url = result.data?.createCard?.url ?? '';

          const resolvedUrl = await smartCardClient
            .fetchData(url)
            .catch(() => {});

          if (resolvedUrl?.meta) {
            linkCardProvider =
              resolvedUrl.meta.key || resolvedUrl.meta.definitionId;
          }
        }

        Analytics.sendTrackEvent({
          action: 'created',
          actionSubject: 'card',
          source: 'inlineCardComposerInlineDialog',
          attributes: {
            taskId: traceId,
            cardRole,
            linkCardProvider,
            containerType: isInboxBoard ? 'inbox' : undefined,
          },
          containers: formatContainers({
            idCard: newCardId,
            idList: listId,
            idBoard: boardId,
            idOrganization: workspaceId,
          }),
        });

        Analytics.taskSucceeded({
          taskName: 'create-card/list',
          source: 'inlineCardComposerInlineDialog',
          traceId,
        });

        if (shouldOpenCardBackOnCreation) {
          if (!newCardId) return;

          Analytics.sendPressedShortcutEvent({
            shortcutName: 'createAndOpenCardShortcut',
            keyValue: Key.Enter,
            source: 'inlineCardComposerInlineDialog',
            containers: formatContainers({
              idCard: newCardId,
              idList: listId,
              idBoard: boardId,
              idOrganization: workspaceId,
            }),
          });

          openCardBack(newCardId);
        }
      } catch (error) {
        cardComposerInputValueState.setValue(cardName);
        Analytics.taskFailed({
          taskName: 'create-card/list',
          traceId,
          source: 'inlineCardComposerInlineDialog',
          error,
        });
      }
    },
    [
      nextPosition,
      updateDraft,
      addCardToList,
      listId,
      boardId,
      isInboxBoard,
      workspaceId,
      position,
      filters,
      setCardIdsAddedSinceFiltering,
      cardIdsAddedSinceFiltering,
    ],
  );

  const [multipleCount, setMultipleCount] = useState(0);
  const [openOnCreation, setOpenOnCreation] = useState(false);

  const saveCard = useCallback(
    async (shouldOpenCardBackOnCreation: boolean = false) => {
      const cardName = cardComposerInputValueState.value.trim();

      if (!cardName) {
        closeCardComposer();
        return;
      }
      const cardTitles = cardName.split(/\r\n|\r|\n/).filter(Boolean);

      if (cardTitles.length === 1) {
        return saveSingleCard(shouldOpenCardBackOnCreation, cardName);
      }
      setMultipleCount(cardTitles.length);
      setOpenOnCreation(shouldOpenCardBackOnCreation);
      show();
    },
    [saveSingleCard, show],
  );

  const saveCardsFromMultiplePopover = useCallback(
    async (saveMultiple: boolean = false) => {
      const shouldOpenCardBackOnCreation = openOnCreation;
      setOpenOnCreation(false);
      const cardName = cardComposerInputValueState.value.trim();
      if (!saveMultiple) {
        return saveSingleCard(
          shouldOpenCardBackOnCreation,
          cardName.replace(/(\r\n|\n|\r)/gm, ' '),
        );
      }
      let updatePosition;
      let isAtBottom = false;
      if (!isFinite(nextPosition)) {
        updatePosition = position + SPACING;
        isAtBottom = true;
      } else {
        updatePosition = (position + nextPosition) / 2;
      }
      const cardTitles = cardName.split(/\r\n|\r|\n/).filter(Boolean);
      for (const [idx, title] of cardTitles.entries()) {
        const lastCard = idx === cardTitles.length - 1;
        const shouldOpen = shouldOpenCardBackOnCreation && lastCard;
        await saveSingleCard(shouldOpen, title, updatePosition);
        if (isAtBottom) {
          updatePosition += SPACING;
        } else {
          updatePosition = (updatePosition + nextPosition) / 2;
        }
      }
      Analytics.sendTrackEvent({
        action: 'created',
        actionSubject: 'multipleCards',
        source: 'createMultipleCardsInlineDialog',
        attributes: {
          numCardsLength: cardTitles.length,
        },
        containers: formatContainers({
          idBoard: boardId,
          idList: listId,
          idOrganization: workspaceId,
        }),
      });
      hide();
      cardComposerState.setValue({ position: updatePosition });
    },
    [
      boardId,
      hide,
      listId,
      nextPosition,
      openOnCreation,
      position,
      saveSingleCard,
      workspaceId,
    ],
  );

  const onChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    cardComposerInputValueState.setValue(e.target.value);
  }, []);

  const onClickCancel = useCallback(() => {
    updateDraft(null);
    cardComposerInputValueState.setValue('');
    closeCardComposer();
  }, [updateDraft]);

  const onKeyDown = useCallback<KeyboardEventHandler<HTMLTextAreaElement>>(
    (e) => {
      if (getKey(e) === Key.Enter) {
        e.preventDefault();
        saveCard(e.shiftKey);
      }
    },
    [saveCard],
  );

  const onPaste = useCallback<ClipboardEventHandler<HTMLTextAreaElement>>(
    (e) => {
      const pastedFiles = e.clipboardData?.files;
      const includesText = e.clipboardData?.types.includes('text/plain');

      if (!pastedFiles?.length || includesText) {
        return;
      }

      e.stopPropagation();
      e.preventDefault();

      let updatePosition: number;
      let isAtBottom = false;
      if (!isFinite(nextPosition)) {
        updatePosition = position + SPACING;
        isAtBottom = true;
      } else {
        updatePosition = (position + nextPosition) / 2;
      }

      Array.from(pastedFiles).forEach((file) => {
        pasteFileOnList(file, listId, updatePosition);
        if (isAtBottom) {
          updatePosition += SPACING;
        } else {
          updatePosition = (updatePosition + nextPosition) / 2;
        }
      });
      cardComposerState.setValue({ position: updatePosition });
    },
    [listId, nextPosition, pasteFileOnList, position],
  );

  const onSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      e.preventDefault();
      saveCard();
    },
    [saveCard],
  );

  const handleEscape = useCallback(() => {
    const currentValue = cardComposerInputValueState.value.trim();
    updateDraft(currentValue ? { title: currentValue } : null);
    cardComposerInputValueState.setValue('');
    closeCardComposer();
  }, [updateDraft]);

  useShortcut(handleEscape, {
    scope: Scope.Board,
    key: Key.Escape,
  });

  const onClickOutside = useCallback(() => {
    if (popoverProps.isVisible) {
      return;
    }
    const currentValue = cardComposerInputValueState.value.trim();
    if (currentValue === '') {
      updateDraft(null);
    } else {
      saveCard();
    }
    cardComposerInputValueState.setValue('');
    closeCardComposer();
  }, [popoverProps.isVisible, saveCard, updateDraft]);

  useClickOutside({ ref, handleClickOutside: onClickOutside });

  const hideSaveMultiplePopover = useCallback(
    (reason: HideReasonType) => {
      if (reason === HideReason.CLICK_OUTSIDE) {
        if (multipleCount > MAX_CARDS) {
          hide();
          return;
        }
        saveCardsFromMultiplePopover(false);
      }
      hide();
    },
    [hide, multipleCount, saveCardsFromMultiplePopover],
  );

  const isAtOrOverLimits = useCardComposerLimits();
  if (isAtOrOverLimits) {
    return (
      <li className={styles.container} ref={ref}>
        <CardComposerLimitError />
      </li>
    );
  }

  return (
    <li
      className={cx(styles.container, {
        [styles.hoverGapEnabled]: isHoverGapEnabled,
      })}
      ref={ref}
    >
      <form onSubmit={onSubmit}>
        <AutosizeTextarea
          // The amount in pixels added by each row of text in a card name:
          bufferHeight={20}
          className={styles.cardComposer}
          dir="auto"
          placeholder={intl.formatMessage({
            id: 'templates.card_composer_inline.enter-a-title-or-paste-a-link',
            defaultMessage: 'Enter a title or paste a link',
            description: 'Text inside card composer component',
          })}
          onFocus={onFocus}
          shouldFocus={true}
          value={inputValue}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          data-testid={getTestId<ListTestIds>('list-card-composer-textarea')}
          ref={targetRef}
        />
        <div className={styles.cardComposerSubmit}>
          <Button
            testId={getTestId<ListTestIds>(
              'list-card-composer-add-card-button',
            )}
            appearance="primary"
            type="submit"
          >
            <FormattedMessage
              id="templates.card_composer_inline.add-card"
              defaultMessage="Add card"
              description="Button text for creating a new card"
            />
          </Button>
          <Button
            onClick={onClickCancel}
            iconBefore={<CloseIcon size="medium" />}
            appearance="subtle"
            aria-label={intl.formatMessage({
              id: 'templates.card_composer_inline.cancel',
              defaultMessage: 'Cancel',
              description: 'Button to cancel creating a new card',
            })}
            testId={getTestId<ListTestIds>('list-card-composer-cancel-button')}
          />
          <Popover
            {...popoverProps}
            onHide={hideSaveMultiplePopover}
            title="Create"
          >
            <SaveMultiplePopover
              count={multipleCount}
              saveCallback={saveCardsFromMultiplePopover}
              closeCallback={hide}
            />
          </Popover>
        </div>
      </form>
    </li>
  );
};
